import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Flag, ShieldX } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { RelativeTime } from '@/components/DataDisplay'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import {
  fetchReports,
  fetchReport,
  resolveReport,
  type AdminReport,
} from '@/lib/adapters'
import { fetchUnfoundedComplaints, TK_STR as S } from './ticketsApi'

const reportStatus = (s: string) =>
  s === 'pending' ? 'pending' : s === 'resolved' ? 'approved' : s === 'dismissed' ? 'rejected' : 'review'

const statusColumn: AdminColumn<AdminReport> = {
  key: 'status',
  header: S.status,
  type: 'enum',
  width: 130,
  options: [
    { value: 'pending', label: S.pendingLbl, status: 'pending' },
    { value: 'resolved', label: S.resolve, status: 'approved' },
    { value: 'dismissed', label: S.dismiss, status: 'rejected' },
  ],
}

function reporterCell(r: AdminReport) {
  return (
    <Link to={`/users/${r.reporter_id}`} className="text-accent hover:underline">
      {r.reporter_username ? `@${r.reporter_username}` : `#${r.reporter_id}`}
    </Link>
  )
}

/** A7.01 — Shikoyatlar ro'yxati. JONLI GET /admin/reports. */
export function ComplaintsList() {
  const navigate = useNavigate()
  const rowActions = useMemo<RowAction<AdminReport>[]>(
    () => [{ id: 'open', label: S.open_, icon: <Flag size={13} />, onSelect: (r) => navigate(`/complaints/${r.id}`) }],
    [navigate],
  )
  const columns = useMemo<AdminColumn<AdminReport>[]>(
    () => [
      { key: 'id', header: '#', type: 'id', width: 70 },
      { key: 'category', header: S.category, type: 'text', width: 130 },
      {
        key: 'target',
        header: S.target,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) => (
          <span className="font-mono text-xs text-secondary">{r.target_type}:{r.target_id}</span>
        ),
      },
      { key: 'reason', header: S.reason, type: 'text', minWidth: 200, sortable: false },
      { key: 'reporter_id', header: S.reporter, type: 'text', width: 130, sortable: false, render: reporterCell },
      statusColumn,
      { key: 'created_at', header: S.created, type: 'date', width: 150 },
    ],
    [],
  )
  const fetcher = useCallback(fetchReports, [])
  return (
    <>
      <PageHeader title={S.complaints} screenId="A7.01" description={S.complaintsNote} />
      <DataTable<AdminReport>
        screenId="A7.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        onRowClick={(r) => navigate(`/complaints/${r.id}`)}
        exportable
        emptyState={{ icon: <Flag size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/** A7.05 — Asossiz shikoyatlar (rad etilgan). JONLI /admin/reports?status=dismissed. */
export function UnfoundedComplaints() {
  const navigate = useNavigate()
  const columns = useMemo<AdminColumn<AdminReport>[]>(
    () => [
      { key: 'id', header: '#', type: 'id', width: 70 },
      { key: 'category', header: S.category, type: 'text', width: 130 },
      { key: 'reporter_id', header: S.reporter, type: 'text', width: 130, sortable: false, render: reporterCell },
      { key: 'reason', header: S.reason, type: 'text', minWidth: 180, sortable: false },
      { key: 'resolution', header: S.resolution, type: 'text', minWidth: 180, sortable: false },
      { key: 'created_at', header: S.created, type: 'date', width: 150 },
    ],
    [],
  )
  const fetcher = useCallback(fetchUnfoundedComplaints, [])
  return (
    <>
      <PageHeader
        title={S.unfounded}
        screenId="A7.05"
        description={S.unfoundedNote}
        actions={
          <Link to="/complaints" className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary">
            <ChevronLeft size={13} />
            {S.complaints}
          </Link>
        }
      />
      <DataTable<AdminReport>
        screenId="A7.05"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        onRowClick={(r) => navigate(`/complaints/${r.id}`)}
        exportable
        emptyState={{ icon: <ShieldX size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/** A7.02 — Shikoyat kartasi + qaror (resolve/dismiss). GET /admin/reports/:id + POST /resolve. */
export function ComplaintCard() {
  const { can } = useAuth()
  const { toast } = useToast()
  const { id = '' } = useParams()
  const [rep, setRep] = useState<AdminReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchReport(Number(id), ac.signal)
      .then((r) => alive && setRep(r))
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const decide = async (dismiss: boolean) => {
    setBusy(true)
    try {
      await resolveReport(Number(id), note.trim(), dismiss)
      toast(S.done, { tone: 'success' })
      setNote('')
      setReload((n) => n + 1)
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  const closed = rep && rep.status !== 'pending'

  return (
    <>
      <PageHeader
        title={rep ? `#${rep.id} · ${rep.category}` : S.complaint}
        screenId="A7.02"
        actions={
          <Link to="/complaints" className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary">
            <ChevronLeft size={13} />
            {S.complaints}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-2xl space-y-2">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !rep ? (
          <ErrorState title={S.notFound} />
        ) : (
          <div className="max-w-2xl space-y-3">
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-border-default bg-surface-2 p-3 text-sm">
              <Field label={S.status}><StatusBadge status={reportStatus(rep.status)} /></Field>
              <Field label={S.category}><span className="text-primary">{rep.category}</span></Field>
              <Field label={S.reporter}>{reporterCell(rep)}</Field>
              <Field label={S.target}><span className="font-mono text-xs text-secondary">{rep.target_type}:{rep.target_id}</span></Field>
              <Field label={S.created}><RelativeTime value={rep.created_at} className="text-secondary" /></Field>
              {rep.resolution ? <Field label={S.resolution}><span className="text-secondary">{rep.resolution}</span></Field> : <span />}
            </div>

            {rep.reason && (
              <div className="rounded-lg border border-border-default bg-surface-2 p-3">
                <div className="mb-1 text-xs text-tertiary">{S.reason}</div>
                <div className="whitespace-pre-wrap text-sm text-primary">{rep.reason}</div>
              </div>
            )}

            {!closed && (
              <div className="space-y-2 rounded-lg border border-border-default bg-surface-2 p-3">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={S.resolvePlaceholder}
                  rows={3}
                  className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary outline-none focus:border-accent"
                />
                <div className="flex gap-2">
                  <button
                    disabled={busy}
                    onClick={() => decide(false)}
                    className="rounded-md border border-success bg-success/10 px-3 py-1.5 text-sm text-success hover:bg-success hover:text-white disabled:opacity-50"
                  >
                    {S.resolve}
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => decide(true)}
                    className="rounded-md border border-danger bg-danger/10 px-3 py-1.5 text-sm text-danger hover:bg-danger hover:text-white disabled:opacity-50"
                  >
                    {S.dismiss}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-tertiary">{label}</span>
      <span>{children}</span>
    </div>
  )
}
